/**
 * Shared cache-invalidation helper for the step-approval mutations.
 *
 * Approving or unapproving a step shifts the aggregate approval progress of
 * every folder that contains the asset — the folder list rows and the folder
 * detail stats block both read `folder.approvalProgress` (`workflowBreakdown` +
 * `inReviewCount`). This mirrors the identical helper in
 * `features/assign-asset-approval-chain`; FSD forbids importing across feature slices,
 * so the strategy is duplicated here rather than shared.
 *
 *   - the folder-list prefix (every root + nested list page shows the bar),
 *   - the folder's own detail (its aggregate), and
 *   - each breadcrumb ancestor's detail — approvalProgress rolls the whole
 *     subtree up via a recursive CTE on the BE, so ancestors go stale too.
 *
 * Ancestors are read from the cached folder detail. `invalidateQueries` marks
 * entries stale without removing them, so reading the cache immediately after is
 * safe; the `?.` guard covers a folder whose detail was garbage-collected (the
 * broad list invalidation already covers list-level staleness in that case).
 */
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import type { QueryClient } from '@tanstack/react-query';

import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';

export function invalidateFolderAggregates(
  queryClient: QueryClient,
  projectId: string,
  folderId: string | undefined,
): void {
  // Every folder-list page (root + nested) shows the aggregate bar. The list key
  // is `['project', projectId, 'folders', 'list', parentId]`; slicing to the
  // first 4 tuple elements invalidates all pages regardless of `parentId`.
  void queryClient.invalidateQueries({
    queryKey: PROJECT_FOLDER_QUERY_KEYS.list(projectId).slice(0, 4),
  });

  if (!folderId) return;

  void queryClient.invalidateQueries({
    queryKey: PROJECT_FOLDER_QUERY_KEYS.detail(projectId, folderId),
  });

  const folder = queryClient.getQueryData<FolderResponseDto>(
    PROJECT_FOLDER_QUERY_KEYS.detail(projectId, folderId),
  );
  folder?.breadcrumbs.forEach((crumb) => {
    void queryClient.invalidateQueries({
      queryKey: PROJECT_FOLDER_QUERY_KEYS.detail(projectId, crumb.id),
    });
  });
}
